import { runAz } from "./az.js";
import { QUOTA_INVITATION } from "./model-cascade.js";

export interface FounderIdentity {
  name: string;
  /** Best-effort contact email; "" when no usable candidate. */
  email: string;
}

/**
 * The two note "invitation" sentences the intake persona treats as pre-approved flags: their
 * verbatim presence inside an injected note turns a conditional offer on (see the persona's
 * subscription-inventory and chosen-model sections). QUOTA_INVITATION is imported from its
 * producer (model-cascade); the subscription-inventory note has no producer module yet, so its
 * sentence is pinned here — keep it byte-identical to the persona and to
 * persona-intake-content.test.ts's INVENTORY_INVITATION (grep the sentence to find all three).
 */
const INVENTORY_INVITATION = "You can offer to walk the founder through these resources.";
const NOTE_INVITATIONS = [QUOTA_INVITATION, INVENTORY_INVITATION] as const;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Strip any note-authorizing invitation sentence from founder-controlled text before it is
 * interpolated into an injected note. The persona has prose defenses against note-borne
 * injection, but the producer should never emit the trigger in the first place: an Entra
 * display name is attacker-controllable, and a crafted one embedding one of these sentences
 * would otherwise forge the exact flag the persona gates a conditional offer on. Cheap,
 * verbatim, case-insensitive; collapses the whitespace the removal leaves behind.
 */
export function stripNoteInvitations(value: string): string {
  let out = value;
  for (const sentence of NOTE_INVITATIONS) {
    out = out.replace(new RegExp(escapeRegExp(sentence), "gi"), " ");
  }
  return out.replace(/\s{2,}/g, " ").trim();
}

/**
 * Derive a contact-email candidate from an Azure identity:
 *  1. the real `mail` attribute (work/school account), else
 *  2. un-mangle a B2B guest UPN — `jane_acme.com#EXT#@host` → `jane@acme.com`
 *     (Azure AD encodes the original email with `@`→`_` before `#EXT#`), else
 *  3. a UPN that already looks like a plain email, else
 *  4. "" (caller cold-asks).
 */
export function deriveEmailCandidate(raw: { mail?: string | null; upn?: string | null }): string {
  const mail = (raw.mail ?? "").trim();
  if (mail) return mail;
  const upn = (raw.upn ?? "").trim();
  if (!upn) return "";
  const ext = upn.indexOf("#EXT#");
  if (ext > 0) {
    const local = upn.slice(0, ext);                 // jane_acme.com
    const at = local.lastIndexOf("_");
    return at > 0 ? `${local.slice(0, at)}@${local.slice(at + 1)}` : "";
  }
  return upn.includes("@") ? upn : "";
}

/** Read the signed-in user's name + email candidate; degrades to empty on any failure. */
export async function getSignedInUserIdentity(
  runAzImpl: (args: string[]) => Promise<string> = runAz,
): Promise<FounderIdentity> {
  try {
    const raw = await runAzImpl([
      "ad", "signed-in-user", "show",
      "--query", "{displayName:displayName, mail:mail, upn:userPrincipalName}",
      "-o", "json",
    ]);
    const p = JSON.parse(raw) as { displayName?: string; mail?: string | null; upn?: string | null };
    return { name: (p.displayName ?? "").trim(), email: deriveEmailCandidate({ mail: p.mail, upn: p.upn }) };
  } catch {
    return { name: "", email: "" };
  }
}

/** The deploy-time {{founder_identity_note}} for the intake agent's instructions. */
export function composeFounderIdentityNote(id: FounderIdentity): string {
  // Both values are interpolated into a note, so both are sanitized — the name is the real
  // attacker vector (Entra displayName), and an email can't hold the space-bearing sentences
  // anyway, so stripping it is a harmless belt-and-suspenders.
  const name = stripNoteInvitations(id.name);
  const email = stripNoteInvitations(id.email);
  if (name && email) {
    return `You're speaking with **${name}**, signed in to Azure (their email looks like **${email}**). ` +
      `Open by confirming both, in your own voice — e.g. "I see you're signed in as ${name}, and I'll use ${email} ` +
      `for your advisor handoffs and cost reports — is that right, or should I change either?" Record exactly what they approve or correct.`;
  }
  if (name) {
    return `You're speaking with **${name}** (signed in to Azure). Confirm their name, then ask for the best contact email ` +
      `to reach them (that's where advisor handoffs and cost reports go).`;
  }
  return `Ask the founder their name and the best contact email to reach them (that's where advisor handoffs and cost reports go).`;
}
